import { observable, action, makeObservable } from 'mobx';
import { localStorageHandler } from 'core/services';
import { BaasicTableSettingsViewStore } from 'common/stores';
import { BaseListViewStore } from 'core/stores';
import { ConfirmModalParams, ModalParams } from 'core/models';
import { OrderStore, OrderDateRangeViewStore } from 'modules/order/stores';
import { OrderLineStore } from 'modules/order-line/stores';
import { checkPermissions } from 'core/stores';
import { LookupStore } from 'modules/lookup/stores';
import { lookupRouteName, sourceOfChange, channelType } from 'common/constants';
import {
	OrderLineService,
	ChannelService,
	ProductVirtualSkuService,
	ShipmentInfoService,
	ApplicationService,
} from 'common/data';
import {
	TableFilterUtility,
	checkForColumnFilter,
	checkForColumnWidth,
	RemoveFiltersModalUtility,
	notificationError,
	isWarehouse,
	isSuperAdminOrganization,
	excelDateToJSDate,
} from 'core/utils';
import {
	HyperlinkCellData,
	DateTimeCellData,
	MultipleCellData,
	BooleanCellData,
	BuyerRequestedCancelCellData,
	QuantityPerSkuCellData,
} from 'core/stores';
import { TableFilterTemplate, TableFilterNumericTemplate, TableFilterDateTemplate } from 'themes/components';
import { HyperLinkTrackingNumberCellData } from 'core/stores/CellDataStore';
import moment from 'moment';
import XLSX from 'xlsx';
import * as excelJS from 'exceljs';
import _ from 'lodash';

class MarketplaceOrderListViewStore extends BaseListViewStore {
	@observable change = false;
	@observable order = null;
	@observable selectedItem = null;
	@observable showOrderLines = true;
	isSuperAdmin = false;
	hasPiiAccess = false;
	showActions = true;
	isExpand = true;
	showTodayShipments;
	selectedItem = null;
	localStorageWidthKey = 'marketplace-orders-column-width';
	@observable orderTypeOptions = [];
	@observable orderStatusOptions = [];

	constructor(rootStore) {
		super(rootStore, {
			listStore: new OrderStore(rootStore),
			queryParams: {
				embed: [
					'shippingCoreCountry',
					'shippingCoreCountryState',
					'channel',
					'channel.timezone',
					'channel.channelType',
					'confirmationType',
					'orderType',
					'orderLines',
					'orderLines.product',
					'orderLines.orderLineStatus',
					'orderStatus',
					'fulfillmentOrders',
					'fulfillmentOrders.shipmentInfos',
					'fulfillmentOrders.shipmentInfos.shipmentInfoStatus',
					'fulfillmentOrders.carrierService',
					'orderLines',
					'seller',
					'shipmentInfoReturns',
					'shipmentInfoReturns.returnedItems',
					'shipmentInfoReturns.returnedItems.product',
				],
				filter: localStorageHandler.get('marketplace-orders-filter'),
			},
			findFunction: 'findOrder',
			tableData: {
				columns: [
					{
						key: 'orderNumber',
						checkPartiallyFulfillMixedOrders: true,
						title: 'ORDERVIEW.ORDERNUMBER',
						onClick: dataItem => this.openDetailPage(dataItem.id),
						dataCell: HyperlinkCellData,
						selected: true,
						expandable: true,
						width: 200,
						filter: null,
						locked: true,
						filterType: TableFilterTemplate,
						sortable: true,
					},
					{
						key: 'customerServiceOrderNumber',
						title: 'ORDERVIEW.CUSTOMERSERVICEORDERNUMBER',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
					},
					{
						key: 'source',
						title: 'ORDERVIEW.CHANNELSOURCE',
						service: 'orderService',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						dontSplit: true,
					},
					{
						key: 'warehouseInfo',
						title: 'ORDERVIEW.WAREHOUSEINFO',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
					},
					{
						key: 'orderDate',
						title: 'ORDERVIEW.ORDERDATE',
						dataCell: DateTimeCellData,
						dateFormat: 'LLL',
						useLocalTime: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterDateTemplate,
						sortable: true,
					},
					{
						key: 'shipByDate',
						title: 'ORDERVIEW.SHIPBYDATE',
						dataCell: DateTimeCellData,
						dateFormat: 'MM/DD/YYYY',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterDateTemplate,
						sortable: true,
					},
					{
						key: 'deliverByDate',
						title: 'ORDERVIEW.DELIVERBYDATE',
						dataCell: DateTimeCellData,
						dateFormat: 'MM/DD/YYYY',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterDateTemplate,
						sortable: true,
					},
					{
						key: 'productQuantityPerSku',
						title: 'ORDERVIEW.ORDERLINESKU',
						selected: false,
						width: 200,
						filter: null,
						dataCell: QuantityPerSkuCellData,
						filterType: TableFilterTemplate,
						sortable: true,
						service: 'productVirtualSkuService',
						dontSplit: true,
					},
					{
						key: 'orderLines.quantity',
						title: 'ORDERVIEW.ORDERLINEQUANTITY',
						embed: ['orderLines'],
						service: 'orderLineService',
						dataCell: MultipleCellData,
						selected: false,
						width: 200,
						filter: null,
						dontSplit: true,
						filterType: TableFilterNumericTemplate,
						sortable: true,
					},
					{
						key: 'fulfillmentOrders.shipmentInfos.trackingNumber',
						title: 'ORDERVIEW.TRACKINGNUMBER',
						selected: false,
						width: 200,
						filter: null,
						carrierServicePath: 'fulfillmentOrders[0].carrierService.name',
						checkForMultiple: true,
						dataCell: HyperLinkTrackingNumberCellData,
						filterType: TableFilterTemplate,
						sortable: true,
						embed: ['fulfillmentOrders, fulfillmentOrders.shipmentInfos'],
						service: 'shipmentInfoService',
						dontSplit: true,
					},
					{
						key: 'shippingCoreCountryState.name',
						title: 'ORDERVIEW.STATE',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						lookupConstant: lookupRouteName.state,
						dontSplit: true,
					},
					{
						key: 'channel.name',
						title: 'ORDERVIEW.CHANNEL',
						service: 'channelService',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'channel.channelType.name',
						title: 'ORDERVIEW.CHANNELTYPE',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						lookupConstant: lookupRouteName.channelType,
					},
					{
						key: 'seller.applicationTitle',
						title: 'ORDERVIEW.SELLER',
						service: 'applicationService',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						sellerOnly: true,
						dontSplit: true,
						enabled: isSuperAdminOrganization(rootStore),
					},
					{
						key: 'orderType.name',
						title: 'ORDERVIEW.ORDERTYPE',
						selected: false,
						sortable: true,
						width: 200,
						filterType: TableFilterTemplate,
						filter: null,
						lookupConstant: lookupRouteName.orderType,
						sortable: true,
					},
					{
						key: 'confirmationType.name',
						title: 'ORDERVIEW.CONFIRMATIONTYPE',
						selected: false,
						sortable: true,
						width: 200,
						filterType: TableFilterTemplate,
						filter: null,
						lookupConstant: lookupRouteName.confirmationType,
					},
					{
						key: 'orderStatus.name',
						title: 'ORDERVIEW.ORDERSTATUS',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						lookupConstant: lookupRouteName.orderStatus,
					},
					{
						key: 'isPrime',
						title: 'ORDERVIEW.ISPRIME',
						dataCell: BooleanCellData,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
					},
					{
						key: 'partnerPO',
						title: 'ORDERVIEW.REFERENCENUMBER',
						service: 'orderService',
						selected: false,
						checkForWarehouseLogin: true,
						width: 200,
						filterType: TableFilterTemplate,
						filter: null,
						isClickable: true,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'orderLines.isBuyerRequestedCancel',
						title: 'ORDERVIEW.ISBUYERREQUESTEDCANCEL',
						dataCell: BuyerRequestedCancelCellData,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						service: 'orderLines',
					},
					{
						key: 'requiredWarehouse',
						title: 'ORDERVIEW.REQUIREDWAREHOUSE',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
					},
				],
				actions: {
					onSplit: async item => this.openSplitConfirmModal(item),
					onEdit: async item => this.routes.edit(item.id),
					onCancelOrder: async item => this.openCancelAllOrderLinesConfirmModal(item),
					onCreateReturnLabel: async item => this.openCreateReturnLabelPage(item),
					onReplace: async item => this.openReplacementModal(item),
				},
				sortObject: [
					{
						field: 'orderNumber',
						dir: 'asc',
					},
				],
			},
			routes: {
				list: () => this.rootStore.routerStore.goTo('master.app.main.order.marketplace.list', {}),
				create: () => this.rootStore.routerStore.goTo('master.app.main.order.marketplace.list.add', {}),
				edit: id =>
					this.rootStore.routerStore.goTo('master.app.main.order.list.edit', {
						id,
					}),
			},
			permissions: {
				update: () => this.authStore.hasPermission('marketplaceOrderSection.update'),
				create: () => this.authStore.hasPermission('marketplaceOrderSection.create'),
				remove: () => this.authStore.hasPermission('marketplaceOrderSection.delete'),
				read: () => this.authStore.hasPermission('marketplaceOrderSection.read'),
				full: () => this.authStore.hasPermission('marketplaceOrderSection.full'),
			},
		});
		makeObservable(this);
		if (isSuperAdminOrganization(rootStore)) {
			this.isSuperAdmin = true;
			this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		this.setInitialFiltering();
		this.queryUtility.initialize(this.findOrder);
		checkForColumnFilter(localStorageHandler.get('marketplace-order-table-data'), this.tableData.columns);
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.columnFilterSetup = 'marketplace-order-table-data';
		this.hasPiiAccess = this.rootStore.authStore.hasPiiAccess;
		this.lookupStore = new LookupStore(rootStore);
		this.orderLineStore = new OrderLineStore(rootStore);
		this.orderDateRangeViewStore = new OrderDateRangeViewStore({
			queryUtility: this.queryUtility,
			findFunction: this.findOrder,
			localStorageHandlerKey: 'mo-date-filter-option',
		});
		this.removeFiltersModal = new RemoveFiltersModalUtility({ viewStore: this });
		this.orderStore = new OrderStore(rootStore);
		this.showActions = checkPermissions(this).actionsAllowed;
		this.isWarehouse = isWarehouse(rootStore);
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.tableFilter = new TableFilterUtility({
			lookupStore: this.lookupStore,
			listStore: this.listStore,
			queryUtility: this.queryUtility,
			columns: this.tableData.columns,
			services: [
				this.listStore.service,
				new OrderLineService(this.rootStore.app.baasic.apiClient),
				new ChannelService(this.rootStore.app.baasic.apiClient),
				new ProductVirtualSkuService(this.rootStore.app.baasic.apiClient),
				new ShipmentInfoService(this.rootStore.app.baasic.apiClient),
				new ApplicationService(this.rootStore.app.baasic.apiClient),
			],
		});
		this.setButtons();
		this.setHeaderModes();
		this.setSearchVisible();
	}

	openDetailPage = id => {
		this.rootStore.routerStore.goTo('master.app.main.order.marketplace.list.detail', {
			id: id,
		});
	};

	@action.bound
	setInitialFiltering = async () => {
		const currentFiltering = localStorageHandler.get('marketplace-orders-filter');
		this.showTodayShipments = currentFiltering != null ? currentFiltering.showTodayShipments : true;
	};

	@action.bound
	openCancelAllOrderLinesConfirmModal = item => {
		this.selectedItem = item;
		this.cancelAllOrderLinesConfirmModal.open({
			message: ['ORDERVIEW.CANCELALLORDERLINESCONFIRM'],
		});
	};

	@action.bound
	cancelAllOrderLines = async () => {
		this.suspend();
		try {
			const {
				data: { total, canceled, refunded, message },
			} =
				this.selectedItem.channel.channelType.name == channelType.amazon
					? await this.orderLineStore.cancelAmazonOrder(this.selectedItem.id, sourceOfChange.cancelAllOrderLines)
					: await this.orderLineStore.cancelMiraklOrder(this.selectedItem.id, sourceOfChange.cancelAllOrderLines);

			if (message) {
				this.rootStore.notificationStore.error(message);
			} else {
				this.rootStore.notificationStore.success([
					'ORDERVIEW.CANCELORDERSSUCCESS',
					{ total, canceled, failed: total - (canceled + refunded) },
				]);
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	openSplitConfirmModal = item => {
		this.selectedItem = item;
		this.splitConfirmModal.open({
			message: ['ORDERVIEW.SPLITMARKETPLACEORDER', { orderNumber: item.orderNumber }],
		});
	};

	openLinkOrderLinesConfirmModal = () => {
		this.linkOrderLinesConfirmModal.open({
			message: ['ORDERVIEW.LINKORDERLINESCONFIRM'],
		});
	};

	openImportOrdersModal = () => {
		this.importModal.open();
	};

	@action.bound
	async downloadExcelTemplate() {
		const workbook = new excelJS.Workbook();

		workbook.created = new Date();
		workbook.modified = new Date();

		let sheet = workbook.addWorksheet('ImportOrdersTemplate');

		sheet.columns = [
			{ header: 'OrderNumber', key: 'OrderNumber', width: 15 },
			{ header: 'OrderDate', key: 'OrderDate', width: 15 },
			{ header: 'ShipByDate', key: 'ShipByDate', width: 15 },
			{ header: 'BuyerName', key: 'BuyerName', width: 15 },
			{ header: 'Phone', key: 'Phone', width: 15 },
			{ header: 'ShippingAddress1', key: 'ShippingAddress1', width: 20 },
			{ header: 'ShippingAddress2', key: 'ShippingAddress2', width: 20 },
			{ header: 'ShippingCity', key: 'ShippingCity', width: 15 },
			{ header: 'ShippingZipCode', key: 'ShippingZipCode', width: 20 },
			{ header: 'ShippingCountry', key: 'ShippingCountry', width: 20 },
			{ header: 'ShippingState', key: 'ShippingState', width: 15 },
			{ header: 'Sku', key: 'Sku', width: 15 },
			{ header: 'Quantity', key: 'Quantity', width: 15 },
		];

		var requiredFieldStyle = {
			type: 'pattern',
			pattern: 'solid',
			fgColor: { argb: 'FFC7CE' },
		};

		var borderStyle = {
			top: { style: 'medium', color: { argb: '27788a' } },
			left: { style: 'medium', color: { argb: '27788a' } },
			bottom: { style: 'medium', color: { argb: '27788a' } },
			right: { style: 'medium', color: { argb: '27788a' } },
		};

		sheet.getCell('A1').border = borderStyle;
		sheet.getCell('B1').border = borderStyle;
		sheet.getCell('C1').border = borderStyle;
		sheet.getCell('D1').border = borderStyle;
		sheet.getCell('E1').border = borderStyle;
		sheet.getCell('F1').border = borderStyle;
		sheet.getCell('G1').border = borderStyle;
		sheet.getCell('H1').border = borderStyle;
		sheet.getCell('I1').border = borderStyle;
		sheet.getCell('J1').border = borderStyle;
		sheet.getCell('K1').border = borderStyle;
		sheet.getCell('L1').border = borderStyle;
		sheet.getCell('M1').border = borderStyle;

		sheet.getCell('A1').fill = requiredFieldStyle;
		sheet.getCell('D1').fill = requiredFieldStyle;
		sheet.getCell('E1').fill = requiredFieldStyle;
		sheet.getCell('F1').fill = requiredFieldStyle;
		sheet.getCell('H1').fill = requiredFieldStyle;
		sheet.getCell('I1').fill = requiredFieldStyle;
		sheet.getCell('J1').fill = requiredFieldStyle;
		sheet.getCell('K1').fill = requiredFieldStyle;
		sheet.getCell('L1').fill = requiredFieldStyle;
		sheet.getCell('M1').fill = requiredFieldStyle;

		sheet.getCell('B1').note = 'Must enter date in valid date format (MM/DD/YYYY)';
		sheet.getCell('C1').note = 'Must enter date in valid date format (MM/DD/YYYY)';
		sheet.getCell('J1').note = 'Must enter value in valid format (e.g. United States or US).';
		sheet.getCell('K1').note = 'Must enter value in valid format (e.g. Indiana or IN)';
		sheet.getCell('L1').note = 'Must enter value that exists in the system';

		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], { type: 'applicationi/xlsx' });
		saveAs(blob, 'ImportOrdersTemplate.xlsx');
	}

	@action.bound onFilesDrop = async filesMeta => {
		this.importModal.close();
		if (filesMeta && filesMeta.length == 1) {
			let fileMeta = filesMeta[0];
			let reader = new FileReader();
			let xlsx = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
			let xls = 'application/vnd.ms-excel';
			if (
				fileMeta.type === xlsx ||
				(fileMeta.type === xls && !fileMeta.name.includes('csv', fileMeta.name.length - 3))
			) {
				//Excel processing (excluding csv)
				reader.onload = async evt => {
					this.suspend();
					const workbook = XLSX.read(evt.target.result, { type: 'binary' });
					const firstWorksheet = workbook.Sheets[workbook.SheetNames[0]];
					let data = XLSX.utils.sheet_to_json(firstWorksheet);
					if (data.length > 0) {
						await this.createOrdersFromExcel(data);
						await this.findOrder();
					} else {
						this.rootStore.notificationStore.error(['ORDERVIEW.NOVALIDDATAFORIMPORTFOUND']);
					}
					this.resume();
				};
				try {
					reader.readAsBinaryString(fileMeta);
				} catch (err) {}
			}
		}
	};

	validateRequiredFields = item => {
		if (
			item.hasOwnProperty('OrderNumber') &&
			item.hasOwnProperty('BuyerName') &&
			item.hasOwnProperty('Phone') &&
			item.hasOwnProperty('ShippingAddress1') &&
			item.hasOwnProperty('ShippingCity') &&
			item.hasOwnProperty('ShippingZipCode') &&
			item.hasOwnProperty('ShippingCountry') &&
			(item.hasOwnProperty('ShippingState') && item.hasOwnProperty('Sku') && item.hasOwnProperty('Quantity'))
		) {
			return true;
		} else {
			return false;
		}
	};

	@action.bound
	createOrdersFromExcel = async data => {
		let ordersToCreate = [];
		let orderNumbersForImport = [...new Set(data.map(x => x.OrderNumber))];

		// Group by OrderNumber, then create an order for each group
		let groupedOrders = _.groupBy(data, 'OrderNumber');
		_.forEach(groupedOrders, group => {
			let orders = [];
			let groupHasError = false;
			for (let order of group) {
				if (groupHasError) {
					break;
				}
				const isOrderValid = this.validateRequiredFields(order);
				if (!isOrderValid) {
					groupHasError = true;
					break;
				} else {
					let orderDate = new Date(Date.UTC(0, 0, order.OrderDate - 1));
					let shipByDate = new Date(Date.UTC(0, 0, order.ShipByDate - 1));
					if (
						(order.OrderDate && !moment(orderDate).isValid()) ||
						(order.ShipByDate && !moment(shipByDate).isValid())
					) {
						groupHasError = true;
						break;
					}

					const regex = /^\d+$/;
					if (!String(order.Quantity).match(regex)) {
						groupHasError = true;
						break;
					}

					if (!String(order.Phone).match(regex)) {
						order.Phone = order.Phone.replace(/\D/g, '');
					}

					orders.push(order);
				}
			}

			if (!groupHasError && orders.length > 0) {
				let orderDate;
				let shipByDate;
				let currentDate = new Date();
				let tomorrow = new Date();
				currentDate.setHours(0, 0, 0, 0);
				tomorrow.setDate(currentDate.getDate() + 1);
				tomorrow.setHours(0, 0, 0, 0);

				if (orders[0].OrderDate) {
					let date = excelDateToJSDate(orders[0].OrderDate);
					date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
					orderDate = moment.utc(moment(date)).format();
				} else {
					orderDate = moment.utc(moment(currentDate)).format();
				}

				if (orders[0].ShipByDate) {
					shipByDate = excelDateToJSDate(orders[0].ShipByDate);
				} else {
					shipByDate = tomorrow.toLocaleString();
				}

				let masterOrder = {
					orderNumber: orders[0].OrderNumber,
					orderDate: orderDate,
					shipByDate: shipByDate,
					paymentDate: orderDate,
					buyerName: orders[0].BuyerName,
					buyerPhone: String(orders[0].Phone),
					shippingAddress1: orders[0].ShippingAddress1,
					shippingAddress2: orders[0].ShippingAddress2 != null ? orders[0].ShippingAddress2 : '',
					shippingCity: orders[0].ShippingCity,
					shippingZipCode: orders[0].ShippingZipCode,
					shippingCoreCountry: {
						name: orders[0].ShippingCountry,
					},
					shippingCoreCountryState: {
						name: orders[0].ShippingState,
					},
					orderLines: [],
				};

				for (let order of orders) {
					masterOrder.orderLines.push({
						quantity: order.Quantity,
						sku: order.Sku,
					});
				}
				ordersToCreate.push(masterOrder);
			}
		});

		if (ordersToCreate.length > 0) {
			var createdOrders = await this.listStore.createOrdersBulk(ordersToCreate, sourceOfChange.createOrderFromXLS);
			if (createdOrders == null) {
				this.rootStore.notificationStore.error(['ORDERVIEW.ORDERIMPORT.FAIL']);
			} else {
				this.rootStore.notificationStore.success([
					'ORDERVIEW.ORDERIMPORT.SUCCESS',
					{ successful: createdOrders.length, total: orderNumbersForImport.length },
				]);
			}
		} else {
			this.rootStore.notificationStore.error(['ORDERVIEW.ORDERIMPORT.FAIL']);
		}
	};

	splitMarketplaceOrder = async item => {
		this.suspend();
		try {
			var response = await this.listStore.splitMarketplaceOrder(item.id, sourceOfChange.splitOrderList);
			if (response[0].isSuccess) {
				response[0].inactiveOrderLineIds.length === 0
					? this.rootStore.notificationStore.success(['ORDERVIEW.SPLITMARKETPLACEORDERSUCCESS'])
					: this.rootStore.notificationStore.warning([
							'ORDERVIEW.SPLITMARKETPLACEORDERPARTIALSUCCESS',
							{
								successful: item.orderLines.length - response[0].inactiveOrderLineIds.length,
								total: item.orderLines.length,
							},
					  ]);
			} else {
				this.rootStore.notificationStore.error([
					response[0].message ? response[0].message : 'ORDERVIEW.ORDERINQUEUEFORSPLIT',
				]);
			}
			await this.queryUtility.initialize(false);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	setAsChanged = () => {
		this.change = !this.change;
	};

	@action.bound
	linkOrderLines = async () => {
		this.suspend();
		try {
			const response = await this.orderLineStore.linkAllOrderLines(sourceOfChange.linkAllOrderLines);
			if (response.data != 0) {
				this.rootStore.notificationStore.success(['ORDERVIEW.LINKORDERLINESSUCCESS', { count: response.data }]);
				this.rootStore.viewStore.countUnlinkedOrderLines();
				await this.queryUtility.initialize(false);
			} else {
				this.rootStore.notificationStore.error('ORDERVIEW.NONEWLINKS');
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	setButtons = () => {
		const buttons = {
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-cog spc--right--sml',
					onClick: this.baasicTableSettingsViewStore.openTableSettingsModal,
					text: 'SETTINGS',
				},
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-trash spc--right--sml',
					onClick: () => {
						this.removeFiltersModal.handleRemoveFiltersButtonClick();
					},
					text: 'FILTER.REMOVEALL',
					disabled: !!(this.queryUtility.filter.advancedSearch === undefined),
				},
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-link spc--right--sml',
					onClick: this.openLinkOrderLinesConfirmModal,
					text: 'ORDERVIEW.LINKORDERLINES',
				},
			],
		};
		if (!this.isWarehouse) {
			buttons.buttons.unshift({
				className: 'btn btn--dropdown header__button',
				iconClassName: 'fa fa-file-import spc--right--sml',
				onClick: this.openImportOrdersModal,
				text: 'ORDERVIEW.IMPORTORDERS',
			});
		}
		if (this.hasPiiAccess) {
			buttons.buttons.unshift({
				className: 'btn btn--dropdown header__button',
				iconClassName: 'fa fa-plus spc--right--sml',
				onClick: this.routes.create,
				authorization: 'marketplaceOrderSection.create',
				text: 'ORDERVIEW.NEW',
			});
		}
		this.setButtonComponent(buttons);
	};

	@action.bound
	expandChange = event => {
		const data = this.listStore.data;
		const item = _.find(data, ({ id }) => id === event.dataItem.id);
		item.expanded = !item.expanded;
		this.listStore.setResponseItem(data);
		this.setAsChanged();
	};

	@action.bound
	toggle = selected => {
		this.showOrderLines = selected;
	};

	@action.bound
	onCheck = async check => {
		this.showTodayShipments = check.currentTarget.checked;
		this.orderDateRangeViewStore.todayFilterChange(this.showTodayShipments);
		await this.findOrder();
	};

	@action.bound
	findOrder = async () => {
		this.suspend();
		if (this.baasicTableSettingsViewStore == undefined) {
			this.baasicTableSettingsViewStore = new BaasicTableSettingsViewStore(this.rootStore);
		}
		await this.baasicTableSettingsViewStore.setTemplates(
			this.tableData,
			'MarketplaceOrdersTableSettings',
			this.setAsChanged,
			this.findOrder
		);
		this.queryUtility.setFilterParams(
			this,
			{
				checkPartiallyFulfillMixedOrders: true,
				sortInvalidOrders: false,
				fetchActiveShipments: true,
				showTodayShipments: this.showTodayShipments,
			},
			this.tableFilter
		);
		await this.listStore.findOrder(this.queryUtility.filter);
		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		this.queryUtility.setFilterPersistance(this, 'marketplace-orders-filter', 'marketplace-order-table-data');
		this.setButtons();
		this.resume();
	};

	importModal = new ModalParams({
		onClose: this.closeEditModal,
	});

	//NOT USED // Added because of rendering issues with list page
	refundOrderLineConfirmModal = new ConfirmModalParams({});

	linkOrderLinesConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			await this.linkOrderLines();
		},
	});

	splitConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			await this.splitMarketplaceOrder(this.selectedItem);
		},
	});

	cancelAllOrderLinesConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			await this.cancelAllOrderLines();
		},
	});

	@action.bound
	openCreateReturnLabelPage = item => {
		this.rootStore.routerStore.goTo('master.app.main.order.marketplace.list.return-label', {
			id: item.id,
		});
	};
	@action.bound
	openReplacementModal = async item => {
		this.selectedItem = item;
		this.replacementModal.open();
	};

	replacementModal = new ModalParams();
}

export default MarketplaceOrderListViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/MarketplaceOrderListViewStore.js